import { useEffect, useState } from 'react';
import { Button, Grid, Box, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import WarningIcon from '@mui/icons-material/WarningTwoTone';
import { Capacitor } from '@capacitor/core';
import { isUpgradeAvailable } from 'hooks/useAppUpgradeNotification/useAppUpgradeNotification.utils';
import { useReleaseFlagsLoading } from 'hooks/useReleaseFlags';
import { Loader } from 'components/Loader';
import { AppStoreLinks } from '../../../constants';
import { getCameraActions, PageLayout } from '../../../components/PageLayout';

const useStyles = makeStyles()((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: '0 auto',
    overflowY: 'auto',
    width: '100%',
    maxWidth: '1000px',
  },
  centerContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
}));

export const Content = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [hasUpgrade, setHasUpgrade] = useState(false);
  useEffect(() => {
    const getUpgradeInternal = async (): Promise<void> => {
      const result = await isUpgradeAvailable();
      setHasUpgrade(result);
    };
    getUpgradeInternal();
  }, []);

  const platform = Capacitor.getPlatform();
  const storeLink =
    platform === 'android' ? AppStoreLinks.android : AppStoreLinks.ios;

  const description = hasUpgrade
    ? `${t('pageNotFound.description')} ${t('pageNotFound.updateAppMessage')}`
    : t('pageNotFound.description');
  return (
    <>
      <Helmet>
        <title>{t('pageNotFound.title')}</title>
      </Helmet>

      <Paper elevation={0} className={classes.paper}>
        <Grid
          container
          spacing={2}
          sx={{
            width: '100%',
            justifyContent: 'center',
            alignContent: 'center',
            height: '100%',
          }}
        >
          <Grid size={12}>
            <Box className={classes.centerContainer}>
              <WarningIcon
                color="error"
                sx={{ fontSize: 'min(10rem, 40vw)' }}
              />
            </Box>
          </Grid>
          <Grid size={12}>
            <Box className={classes.centerContainer}>
              <Typography variant="h1">{t('pageNotFound.title')}</Typography>
            </Box>
          </Grid>
          <Grid size={12}>
            <Box className={classes.centerContainer}>
              <Typography>{description}</Typography>
            </Box>
          </Grid>
          <Grid size={12} sx={{ textAlign: 'center' }}>
            <Button
              fullWidth
              size="large"
              href={hasUpgrade ? storeLink : '/'}
              target={hasUpgrade ? '_blank' : '_self'}
              sx={{ maxWidth: '700px' }}
            >
              {hasUpgrade
                ? t('pageNotFound.buttons.update')
                : t('pageNotFound.buttons.home')}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export const PageNotFound = (): JSX.Element => {
  const { t } = useTranslation();
  const flagsLoading = useReleaseFlagsLoading();

  return (
    <PageLayout
      data-cname="NotFoundPage"
      headerBarProps={{
        action: '/',
        title: flagsLoading ? undefined : t('pageNotFound.title'),
        iconActions: getCameraActions(t),
      }}
    >
      {flagsLoading ? <Loader size={48} /> : <Content />}
    </PageLayout>
  );
};
